// A serial (UC-INVENTORY-004, -005; stock.html's serial rows and stock-transfer.html's Serial panel): its product, branch, status
// and where its unit is, its receipt and receipt cost (for reference: the branch values the product at its weighted average, W5),
// the price it sells at now — its own, else the branch's, else the tenant's (UC-PRICING-008) — its tag, notes and barcode value,
// what holds it, a written-off serial's document and Control Log link, and its movements. A stock role edits the tag, the notes
// and the own price while the unit is on hand; a price below its floor asks its reason under the price before saving and then the
// Control PIN (`PriceOverride`), the same save sent again with the token (BR-INVENTORY-014; DVN's answer of 28 Oct 2026 to question
// 52 (b), change 11). An Owner or Admin deletes a serial recorded in error with
// a reason and the Control PIN (`Delete`); one that has moved since it arrived is refused before any PIN (BR-INVENTORY-015).
import { INVENTORY_LIMITS, type SerialView, type StockMovementType } from "@visionx/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { newIdempotencyKey } from "../api/client";
import { products } from "../api/endpoints.catalogue";
import { serials } from "../api/endpoints.inventory";
import { carriedOut, describeError, isApiError } from "../api/problem";
import { formatDate, formatMoney, percentFromBp } from "../lib/format";
import { centsFromText, textFromCents } from "../lib/money-input";
import { useOneStore } from "../session/one-store";
import { can, useSessionView } from "../session/store";
import { useStepUp } from "../stepup/StepUpProvider";
import { Alert, Empty, ErrorAlert, Field, KV, Loading, LockMark, Modal, Panel, TextArea, TextInput } from "../ui/bits";
import { NoAccessPanel, PageHead } from "./common";
import { useLeaveGuard } from "./PatientsShared";
import { belowFloor, useDiscountThresholds } from "./ReceiptsShared";
import { AdjustModal } from "./StockAdjust";
import { ControlLogRef, DocumentRef, MovementItem, STATE_WORD, SerialStatusChip, groupMovements, personWithId, useStockWrites } from "./StockShared";

const dash = <span className="faint">—</span>;

/** A serial's movements other than its arrival, in words, for the deletion's refusal (UC-INVENTORY-005 E1). */
const MOVED_WORD: Partial<Record<StockMovementType, string>> = {
  TransferOut: "sent on a transfer", TransferIn: "received on a transfer", WriteOff: "written off", Adjustment: "adjusted", StockTake: "counted at a finalised count",
  Reserve: "reserved", Release: "released", Sale: "sold", ToLab: "sent to a lab", FromLab: "back from a lab", Return: "returned", CostRevaluation: "revalued",
};

/** The price the unit sells at now and where it comes from (UC-PRICING-008): "R 5 300.00 · tenant band 100%", "R 4 800.00 · own price". */
function priceWords(v: SerialView, band: { bp: number; scope: "Tenant" | "Branch" } | null): ReactNode {
  const p = v.resolvedPrice;
  if (p.priceCents === null) return <span className="faint">No price</span>;
  const money = <span className="mono">{formatMoney(p.priceCents)}</span>;
  if (p.source === "SerialPrice") return <>{money} · own price{v.priceOverriddenBy ? <span className="faint"> · set by {personWithId(v.priceOverriddenBy)}{v.priceOverriddenAt ? ` on ${formatDate(v.priceOverriddenAt)}` : ""}</span> : null}</>;
  if (p.source === "BranchPrice") return <>{money} · {v.branchName}&#39;s price</>;
  if (p.source === "TenantPrice") return <>{money} · {band ? `${band.scope === "Branch" ? `${v.branchName} band` : "tenant band"} ${percentFromBp(band.bp)}` : "tenant price"}</>;
  return money;
}

function holdWords(h: SerialView["holds"][number]): ReactNode {
  const doc = <DocumentRef doc={{ type: h.type, id: h.id, number: h.number }} />;
  if (h.type === "StockTransfer") return <>{h.status === "Draft" ? "On draft" : "In transit on"} {doc}</>;
  if (h.type === "SalesOrder") return <>Reserved for {doc}</>;
  if (h.type === "LabOrder") return <>At a lab on {doc}</>;
  return doc;
}

/** /stock/serials/:id */
export function StockSerial() {
  const s = useSessionView();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const { online, title } = useStockWrites();
  const allowed = can(s, "inventory.serial.read");
  const q = useQuery({ queryKey: ["serial", id], queryFn: () => serials.get(id), enabled: allowed });
  const v = q.data?.data;
  // The band behind a tenant price, for "tenant band 100%" (pricing's figures beside the product, at the acting branch).
  const product = useQuery({ queryKey: ["product", v?.productId], queryFn: () => products.get(v!.productId), enabled: !!v && v.resolvedPrice.source === "TenantPrice" && v.branchId === s?.activeBranchId && can(s, "catalogue.product.read"), staleTime: 60_000 });
  const fig = product.data?.data.atBranch;
  const band = fig && fig.markupBp !== null && fig.markupScope ? { bp: fig.markupBp, scope: fig.markupScope } : null;
  const [dialog, setDialog] = useState<"edit" | "delete" | "adjust" | null>(null);
  const [notice, setNotice] = useState<ReactNode>(null);

  if (!allowed) return <section className="screen" id="s-stock-serial"><div className="content"><PageHead title="Serial" /><NoAccessPanel page="serials" permission="inventory.serial.read" /></div></section>;
  const atActing = !!v && v.branchId === s?.activeBranchId;
  const stockLink = v ? `/stock?product=${v.productId}${v.branchId !== s?.activeBranchId ? `&branch=${v.branchId}` : ""}` : "/stock";
  return (
    <section className="screen" id="s-stock-serial">
      <div className="content">
        <PageHead
          title={v ? <span className="row c gap-8">Serial <span className="mono">{v.serialNumber}</span><SerialStatusChip status={v.status} /></span> : "Serial"}
          sub={v ? <>{v.productName} · <span className="mono">{v.sku}</span> · {v.branchName}</> : undefined}
          actions={<>
            <Link className="btn" to={stockLink}>Back to stock</Link>
            {v && v.onHand && can(s, "inventory.adjustment.write") && <button className="btn" type="button" disabled={!online || !atActing} title={title ?? (atActing ? undefined : `Adjusted at ${v.branchName}`)} onClick={() => setDialog("adjust")}>Adjust<LockMark /></button>}
            {v && v.onHand && can(s, "inventory.serial.write") && <button className="btn" type="button" disabled={!online} title={title} onClick={() => setDialog("edit")}>Edit</button>}
            {v && can(s, "inventory.serial.delete") && <button className="btn danger" type="button" disabled={!online} title={title} onClick={() => setDialog("delete")}>Delete serial…<LockMark /></button>}
          </>}
        />
        {notice && <Alert tone="ok" role="status" actions={<button className="btn sm" type="button" onClick={() => setNotice(null)}>Dismiss</button>}>{notice}</Alert>}
        {q.isPending && <Loading what="the serial" />}
        {q.isError && (isApiError(q.error) && q.error.code === "NOT_FOUND"
          ? <Alert tone="warn"><b>This serial was not found.</b> It may have been deleted, belong to another organisation or be held at a branch you are not assigned to.</Alert>
          : <ErrorAlert error={q.error} />)}
        {v && (
          <div className="split wide">
            <Panel title="Details">
              <KV rows={[
                ["Product", <Link to={`/products/${v.productId}`}>{v.productName}</Link>],
                ["SKU", <span className="mono">{v.sku}</span>],
                ["Branch", v.branchName],
                ["Status", <div className="row c gap-8"><SerialStatusChip status={v.status} />{v.statusChangedAt && <span className="small faint">since {formatDate(v.statusChangedAt)}{v.statusReason ? ` · ${v.statusReason === "StockTakeVariance" ? "stock-take variance" : v.statusReason.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase()}` : ""}</span>}</div>],
                ["Where", v.location === "None" ? <span className="faint">No longer on hand</span> : STATE_WORD[v.location]],
                ["Received", v.receivedOn ? <>{formatDate(v.receivedOn)}{v.supplier ? ` · ${v.supplier.name}` : ""}{v.goodsReceiptId && can(s, "purchasing.po.read") ? <> · <Link to={`/purchasing/receipts/${v.goodsReceiptId}`}>its goods receipt</Link></> : null}</> : dash],
                ["Cost", v.unitCostCents === null ? dash : <><span className="mono">{formatMoney(v.unitCostCents)}</span><div className="small faint">its receipt cost, for reference{v.averageCostCents !== null ? `; ${v.branchName} values the product at its weighted average, ${formatMoney(v.averageCostCents)}` : ""}</div></>],
                ["Selling price", priceWords(v, band)],
                ["Tag", v.tag ? <span className="mono">{v.tag}</span> : dash],
                ["Notes", v.notes ?? dash],
                ["Barcode", <><span className="mono">{v.barcodeValue}</span>{v.source === "SupplierSerial" ? <span className="small faint"> · the supplier&#39;s serial</span> : null}</>],
                ...(v.holds.length > 0 ? [["Held", <>{v.holds.map((h, i) => <span key={`${h.type}-${h.number}`}>{i > 0 ? " · " : ""}{holdWords(h)}</span>)}</>] as [ReactNode, ReactNode]] : []),
                ...(v.writtenOff ? [["Written off", <>{v.writtenOff.at ? formatDate(v.writtenOff.at) : ""}{v.writtenOff.document ? <> · <DocumentRef doc={v.writtenOff.document} /></> : null}{v.writtenOff.controlLogId ? <> · <ControlLogRef /></> : null}</>] as [ReactNode, ReactNode]] : []),
                ...(v.status === "Sold" ? [["Sold", <>{v.soldInvoiceId ? <span className="mono">{v.soldInvoiceId}</span> : null}{v.soldAt ? ` · ${formatDate(v.soldAt)}` : ""}</>] as [ReactNode, ReactNode]] : []),
              ]} />
            </Panel>
            <Panel title="Movements" meta={`${v.serialNumber} · newest first`}>
              {v.movements.length === 0 ? <Empty title="No movements." /> : <div className="list">{groupMovements([...v.movements].sort((a, b) => (a.occurredAt < b.occurredAt ? 1 : -1))).map((g) => <MovementItem key={g[0]!.id} group={g} />)}</div>}
            </Panel>
          </div>
        )}
        {v && dialog === "edit" && (
          <EditSerialModal v={v} onClose={() => setDialog(null)} onDone={(next) => {
            setDialog(null);
            qc.setQueryData(["serial", id], (old: { data: SerialView } | undefined) => (old ? { ...old, data: next } : old));
            void qc.invalidateQueries({ queryKey: ["serials"] });
            setNotice(<b>{next.serialNumber} saved.</b>);
          }} />
        )}
        {v && dialog === "delete" && (
          <DeleteSerialModal v={v} onClose={() => setDialog(null)} onAdjust={() => setDialog("adjust")} onDone={(avg) => {
            setDialog(null);
            void qc.invalidateQueries({ queryKey: ["serials"] });
            void qc.invalidateQueries({ queryKey: ["stock"] });
            qc.removeQueries({ queryKey: ["serial", id] });
            // Deleted with its answer not kept (UC-INVENTORY-005 A2): what the branch now values the product at is not known here.
            navigate(stockLink, { state: { notice: avg === null
              ? `${v.serialNumber} deleted. Its arrival was reversed at its own cost; its number is never issued again.`
              : `${v.serialNumber} deleted. Its arrival was reversed at its own cost; ${v.branchName} values ${v.sku} at ${formatMoney(avg)}. Its number is never issued again.` } });
          }} />
        )}
        {v && dialog === "adjust" && (
          <AdjustModal initial={null} scanFirst={v.serialNumber} onClose={() => setDialog(null)} onDone={(a) => {
            setDialog(null);
            void qc.invalidateQueries({ queryKey: ["serial", id] });
            void qc.invalidateQueries({ queryKey: ["stock"] });
            setNotice(a
              ? <><b><Link className="mono" to={`/stock/adjustments/${a.id}`}>{a.number}</Link> posted.</b> {formatMoney(a.totalValueCents)} at cost.</>
              : <><b>The adjustment was posted: its answer did not arrive.</b> It is listed under Adjustments.</>);
          }} />
        )}
      </div>
    </section>
  );
}

/**
 * UC-INVENTORY-004 steps 3–4, A1–A4: the tag, the notes and the own price; only what changed is sent. A price below its floor —
 * measured from the price the unit sells at now with the discount thresholds as they resolve at the acting branch, the server
 * deciding — asks "Reason for the price" under it before saving, and the save carries it from its first attempt, so the Control PIN
 * dialog asks none (A3, E10; DVN's answer of 28 Oct 2026 to Sprint 4's question 52 (b), change 11). A save the server refuses for
 * want of a reason the edit did not foresee shows the field with the refusal, keeping what was typed (E10 step 2).
 */
function EditSerialModal({ v, onClose, onDone }: { v: SerialView; onClose: () => void; onDone: (next: SerialView) => void }) {
  const withStepUp = useStepUp();
  const oneStore = useOneStore();
  const thresholds = useDiscountThresholds();
  const [tag, setTag] = useState(v.tag ?? "");
  const [notes, setNotes] = useState(v.notes ?? "");
  const [price, setPrice] = useState(textFromCents(v.salesPriceCents));
  const [reason, setReason] = useState("");
  const [reasonAsked, setReasonAsked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [priceErr, setPriceErr] = useState<string | null>(null);
  const [reasonErr, setReasonErr] = useState<string | null>(null);
  const reference = v.resolvedPrice.source === "SerialPrice" ? null : v.resolvedPrice.priceCents;
  const typedNow = price.trim() === "" ? null : centsFromText(price);
  const priceMoves = (price.trim() === "" || typedNow !== null) && typedNow !== v.salesPriceCents;
  // Once the server has asked it, the edit cannot know the floor the server measured from: any price typed may need the PIN, a
  // clearing never (UC-INVENTORY-004 A4, E10 step 2; the verification of 30 Oct 2026, C11-V1).
  const askReason = priceMoves && (belowFloor(v.resolvedPrice.priceCents, typedNow, thresholds) || (reasonAsked && typedNow !== null));
  const submit = async () => {
    if (busy) return;
    const body: { tag?: string | null; notes?: string | null; salesPriceCents?: number | null; reason?: string } = {};
    if (tag.trim() !== (v.tag ?? "")) body.tag = tag.trim() || null;
    if (notes.trim() !== (v.notes ?? "")) body.notes = notes.trim() || null;
    const typed = price.trim() === "" ? null : centsFromText(price);
    if (price.trim() !== "" && typed === null) { setPriceErr("An amount in rand, such as 4800.00"); return; }
    if (typed !== v.salesPriceCents) body.salesPriceCents = typed;
    if (askReason && reason.trim() === "") { setReasonErr("Enter the reason for the price"); return; }
    if (body.salesPriceCents !== undefined && askReason) body.reason = reason.trim();
    if (Object.keys(body).length === 0) { onClose(); return; }
    setBusy(true);
    setError(null);
    setPriceErr(null);
    setReasonErr(null);
    try {
      const before = v.resolvedPrice.priceCents;
      const r = await withStepUp((g) => serials.patch(v.id, body, g), {
        title: `Price override — ${v.serialNumber}`, reason: "none",
        // At a practice of one store, the store's price — as the help says — never a branch price (A9; the check of 30 Oct 2026).
        description: `${v.productName} from ${formatMoney(before)} to ${formatMoney(typed)}: lower than ${oneStore ? `${v.branchName}'s price` : "its branch price"} by more than the discount threshold, so an authoriser's Control PIN is needed. The reason is written to the Control Log with the price before and after.`,
      });
      if (r) onDone(r.data);
    } catch (err) {
      setError(err);
      // E10 step 2: a reason the edit did not foresee the server needing — asked under the price now, what was typed kept.
      const why = isApiError(err) ? err.field("reason") : undefined;
      if (why) { setReasonAsked(true); setReasonErr(why.code === "too_long" ? `At most ${INVENTORY_LIMITS.noteMax} characters` : "Enter the reason for the price"); }
    }
    finally { setBusy(false); }
  };
  const fieldErr = (path: string): string | undefined => (isApiError(error) ? error.field(path)?.message : undefined);
  const gone = isApiError(error) && error.code === "IMMUTABLE_RECORD";
  return (
    <Modal title={`Edit serial ${v.serialNumber}`} meta={v.productName} onClose={() => { if (!busy) onClose(); }} onSubmit={() => void submit()} footer={<>
      <button className="btn" type="button" onClick={onClose} disabled={busy}>Cancel</button>
      <button className="btn primary" type="submit" disabled={busy}>{busy ? "Saving…" : "Save"}</button>
    </>}>
      {error !== null && !fieldErr("tag") && !fieldErr("notes") && !fieldErr("salesPriceCents") && !fieldErr("reason") && <Alert tone="crit"><b>Not saved.</b> {gone ? "This serial is no longer on hand: a sold or written-off unit is history and is not edited." : describeError(error)}</Alert>}
      <Field label="Tag" htmlFor="serial-tag" error={fieldErr("tag")} help="A label to find the frame by — a window or display position · at most 40 characters">
        <TextInput id="serial-tag" value={tag} maxLength={40} onChange={(e) => setTag(e.target.value)} autoFocus />
      </Field>
      <Field label="Notes" htmlFor="serial-notes" error={fieldErr("notes")}>
        <TextArea id="serial-notes" value={notes} rows={3} maxLength={1000} onChange={(e) => setNotes(e.target.value)} />
      </Field>
      <Field label="Own selling price (incl. VAT)" htmlFor="serial-price" error={priceErr ?? fieldErr("salesPriceCents")}
        help={<>Blank sells it at {v.branchName}&#39;s price{reference !== null ? `, ${formatMoney(reference)}` : ""}. A raise needs no PIN; lowering it below the discount threshold needs an authoriser&#39;s <LockMark>Control PIN</LockMark>.</>}>
        <TextInput id="serial-price" mono inputMode="decimal" value={price} placeholder={reference !== null ? textFromCents(reference) : "No price"} onChange={(e) => { setPrice(e.target.value); setPriceErr(null); }} boxClass="w-180" />
      </Field>
      {askReason && (
        <Field label="Reason for the price" required htmlFor="serial-price-reason" error={reasonErr ?? undefined} help="Written on the serial and to the Control Log with the price before and after">
          <TextInput id="serial-price-reason" value={reason} maxLength={INVENTORY_LIMITS.noteMax} error={!!reasonErr} placeholder="e.g. Patient comparing — matching the quote" onChange={(e) => { setReason(e.target.value); setReasonErr(null); }} />
        </Field>
      )}
    </Modal>
  );
}

/**
 * UC-INVENTORY-005: the reason, then the Control PIN (`Delete`, record type `serial`) under one Idempotency-Key per reason; a serial
 * that has moved since it arrived is refused before any PIN, saying to correct it with an adjustment.
 */
function DeleteSerialModal({ v, onClose, onAdjust, onDone }: { v: SerialView; onClose: () => void; onAdjust: () => void; onDone: (averageCents: number | null) => void }) {
  const withStepUp = useStepUp();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [working, setWorking] = useState(false);
  useLeaveGuard(working);
  const [error, setError] = useState<unknown>(null);
  const key = useMemo(() => newIdempotencyKey(), [reason.trim()]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setError(null); }, [reason]);
  const busyRef = useRef(false);
  busyRef.current = busy;
  const close = useCallback(() => { if (!busyRef.current) onClose(); }, [onClose]);
  const submit = async () => {
    const why = reason.trim();
    if (!why || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await withStepUp((g) => { if (g.token) setWorking(true); return serials.void(v.id, why, { ...g, idempotencyKey: key }); }, {
        title: `Delete serial ${v.serialNumber}`, reason: "none",
        description: `${v.serialNumber} leaves every list and search; its arrival is reversed at its own cost and its number is never issued again. The reason is written to the Control Log.`,
      });
      if (r) onDone(r.data.averageCostCents);
    } catch (err) {
      // Deleted again under its key after a lost answer, answered as carried out: deleted once, its answer not kept (the verification of round 3).
      if (carriedOut(err)) onDone(null);
      else setError(err);
    } finally { setBusy(false); setWorking(false); }
  };
  const moved = isApiError(error) && error.code === "IMMUTABLE_RECORD" && error.meta.reason === "has_movements";
  const movedWords = moved && Array.isArray(error.meta.movementTypes)
    ? [...new Set((error.meta.movementTypes as StockMovementType[]).map((t) => MOVED_WORD[t]).filter((w): w is string => !!w))] : [];
  const reasonErr = isApiError(error) ? error.field("reason")?.message : undefined;
  return (
    <Modal title={`Delete serial ${v.serialNumber}`} meta="Owner or Admin · recorded in error" onClose={close} onSubmit={() => void submit()} footer={<>
      <button className="btn" type="button" onClick={close} disabled={busy}>Cancel</button>
      {moved
        ? <button className="btn primary" type="button" onClick={onAdjust}>Adjust instead</button>
        : <button className="btn danger" type="submit" disabled={busy || !reason.trim()}>{busy ? "Deleting…" : "Delete serial"}<LockMark /></button>}
    </>}>
      {working && <Alert tone="info" role="status"><b>Deleting {v.serialNumber}…</b> Keep this page open.</Alert>}
      {moved
        ? <Alert tone="crit"><b>This serial can&#39;t be deleted: it has moved since it arrived{movedWords.length ? ` (${movedWords.join(", ")})` : ""}.</b> Correct it with an adjustment instead.</Alert>
        : error !== null && !reasonErr && <Alert tone="crit"><b>Not deleted.</b> {isApiError(error) && error.meta.reason === "already_deleted" ? "This serial is already deleted." : describeError(error)}</Alert>}
      <Alert tone="warn"><b>For a unit that never existed</b> — captured twice, or at the wrong cost. It is voided, never removed: who, when and why are kept with it, and {v.branchName}&#39;s average backs its cost out.</Alert>
      <Field label="Reason" required htmlFor="serial-delete-reason" error={reasonErr} help="Written on the serial and to the Control Log with the authoriser">
        <TextArea id="serial-delete-reason" value={reason} maxLength={500} rows={2} onChange={(e) => setReason(e.target.value)} autoFocus />
      </Field>
      <div className="help"><LockMark>requires Control PIN</LockMark> — an Owner, Admin or the branch&#39;s Branch owner authorises it.</div>
    </Modal>
  );
}
